import { z } from 'zod';
import { nutrientsSchema } from '../nutrients.js';
export const mealSlotSchema = z.enum(['breakfast', 'lunch', 'dinner', 'snack', 'drink', 'custom']);
export const slotStatusSchema = z.enum(['planned', 'cooked', 'skipped']);
/** Where a place at a meal stands (ADR-016). */
export const attendanceSchema = z.enum(['offered', 'accepted', 'declined']);
export const slotMemberSchema = z.object({
    memberId: z.string().uuid(),
    memberName: z.string(),
    servings: z.number().nullable(),
    grams: z.number().nullable(),
    /** 'offered' means they have been asked and have not answered. */
    state: attendanceSchema,
});
export const planSlotSchema = z.object({
    id: z.string().uuid(),
    date: z.string(),
    slot: mealSlotSchema,
    slotLabel: z.string().nullable(),
    recipeId: z.string().uuid().nullable(),
    recipeTitle: z.string().nullable(),
    foodId: z.string().uuid().nullable(),
    foodName: z.string().nullable(),
    /** A meal in no recipe book, as typed (ADR-042). It has no nutrition. */
    freeText: z.string().nullable().default(null),
    status: slotStatusSchema,
    note: z.string().nullable(),
    position: z.number().int(),
    members: z.array(slotMemberSchema),
});
export const fixedEventSchema = z.object({
    id: z.string().uuid(),
    date: z.string(),
    slot: mealSlotSchema.nullable(),
    memberIds: z.array(z.string().uuid()),
    kind: z.enum(['eating_out', 'fasting', 'away', 'other']),
    note: z.string().nullable(),
});
export const dayTotalsSchema = z.object({
    date: z.string(),
    memberId: z.string().uuid(),
    planned: nutrientsSchema,
});
export const planSchema = z.object({
    /** Null for a week nobody has planned anything into yet. */
    id: z.string().uuid().nullable(),
    weekStart: z.string(),
    status: z.enum(['draft', 'active', 'archived']),
    slots: z.array(planSlotSchema),
    fixedEvents: z.array(fixedEventSchema),
    /** Planned totals per member per day, computed from the slots (M-51). */
    totals: z.array(dayTotalsSchema),
});
export const slotInputSchema = z
    .object({
    date: z.string().date(),
    slot: mealSlotSchema,
    slotLabel: z.string().max(60).nullish(),
    recipeId: z.string().uuid().nullish(),
    foodId: z.string().uuid().nullish(),
    /** Anything at all, for a meal in no recipe book (ADR-042). */
    freeText: z.string().trim().min(1).max(200).nullish(),
    note: z.string().max(300).nullish(),
    position: z.number().int().min(0).max(100).default(0),
    members: z
        .array(z.object({
        memberId: z.string().uuid(),
        servings: z.number().positive().max(20).nullish(),
        grams: z.number().positive().max(5000).nullish(),
    }))
        .min(1, 'A planned meal needs at least one person eating it'),
})
    .refine((v) => [v.recipeId, v.foodId, v.freeText].filter(Boolean).length === 1, {
    message: 'A planned meal is a recipe, a food, or something typed: exactly one of them',
})
    .refine((v) => v.members.every((m) => m.servings != null || m.grams != null), {
    message: 'Every person in a slot needs a portion',
});
export const moveSlotSchema = z.object({
    date: z.string().date(),
    slot: mealSlotSchema,
    position: z.number().int().min(0).max(100).default(0),
});
export const fixedEventInputSchema = z.object({
    date: z.string().date(),
    slot: mealSlotSchema.nullish(),
    memberIds: z.array(z.string().uuid()).default([]),
    kind: z.enum(['eating_out', 'fasting', 'away', 'other']).default('other'),
    note: z.string().max(300).nullish(),
});
export const cookPreviewSchema = z.object({
    planSlotId: z.string().uuid(),
    recipeTitle: z.string(),
    servingsPlanned: z.number(),
    ingredients: z.array(z.object({
        foodId: z.string().uuid(),
        foodName: z.string(),
        gramsNeeded: z.number(),
        /** What is in the house, across every place it is kept. */
        gramsAvailable: z.number(),
        shortfall: z.number(),
        items: z.array(z.object({
            inventoryItemId: z.string().uuid(),
            locationName: z.string(),
            quantity: z.number(),
            takeQuantity: z.number(),
            bestBefore: z.string().nullable(),
        })),
    })),
});
export const cookRequestSchema = z.object({
    /** How many servings were actually made, if it differed from the plan. */
    servingsMade: z.number().positive().max(50).nullish(),
    /** Who ate, and how much. Defaults to the plan. */
    eaten: z
        .array(z.object({ memberId: z.string().uuid(), servings: z.number().min(0).max(20) }))
        .optional(),
    /** Corrections to what came out of the cupboard. */
    adjustments: z
        .array(z.object({ inventoryItemId: z.string().uuid(), takeQuantity: z.number().min(0) }))
        .optional(),
    /** Leftovers go in here. Without it they are not recorded (S-31). */
    leftoverLocationId: z.string().uuid().nullish(),
});
export const cookResultSchema = z.object({
    decremented: z.number().int(),
    leftoverServings: z.number(),
    leftoverItemId: z.string().uuid().nullable(),
    mealsLogged: z.number().int(),
    /** Cooked for, but they have not said yes, so it is not in their records. */
    mealsAwaited: z.number().int(),
    shortfalls: z.array(z.object({ foodName: z.string(), grams: z.number() })),
});
export const logMealSchema = z
    .object({
    memberId: z.string().uuid(),
    date: z.string().date(),
    slot: mealSlotSchema,
    planSlotId: z.string().uuid().nullish(),
    recipeId: z.string().uuid().nullish(),
    foodId: z.string().uuid().nullish(),
    quantity: z.number().positive(),
    /** `item` is a count of a food: a per-item one, or one with a typical weight (ADR-028). */
    unit: z.enum(['g', 'ml', 'serving', 'item']),
    source: z.enum(['planned', 'adjusted', 'substituted', 'ad_hoc']).default('ad_hoc'),
    note: z.string().max(300).nullish(),
    /**
     * A free-form entry: something in no database, with a figure the person
     * asserts (ADR-017). Energy is required because without it there is
     * nothing to count; macros are optional because somebody who knows the
     * calories rarely knows the protein, and demanding all four sends them
     * back to not logging it at all.
     */
    freeForm: z
        .object({
        description: z.string().min(1).max(200),
        energyKcal: z.number().positive().max(20000),
        proteinG: z.number().min(0).max(1000).nullish(),
        carbG: z.number().min(0).max(1000).nullish(),
        fatG: z.number().min(0).max(1000).nullish(),
        /**
         * Roughly how much it was. Given, this stops being a one-off: a food
         * is saved with per-100g values and everything from then on is
         * ordinary computed nutrition (ADR-017).
         */
        saveAsFoodGrams: z.number().positive().max(5000).nullish(),
    })
        .nullish(),
    /**
     * Take what was eaten out of the kitchen's stock of this food, nearest
     * its best-before first. Only for a food; a shortfall is reported, not
     * refused.
     */
    takeFromKitchen: z.boolean().optional(),
})
    .refine((v) => [v.recipeId, v.foodId, v.freeForm].filter(Boolean).length === 1, {
    message: 'Log exactly one of a recipe, a food, or something free-form',
})
    .refine((v) => v.unit !== 'serving' || Boolean(v.recipeId) || Boolean(v.freeForm), {
    message: 'Servings only apply to a recipe',
})
    .refine((v) => v.unit !== 'item' || Boolean(v.foodId), {
    message: 'Only a food can be counted',
})
    .refine((v) => !v.takeFromKitchen || Boolean(v.foodId), {
    message: 'Only a food can be taken from the kitchen',
});
/** What a logged meal took out of the kitchen, when asked to. */
export const kitchenTakeSchema = z.object({
    taken: z.array(z.object({
        inventoryItemId: z.string().uuid(),
        locationName: z.string(),
        quantity: z.number(),
        left: z.number(),
        unit: z.enum(['g', 'ml', 'serving', 'item']),
    })),
    /** What the kitchen did not have, in the unit logged. */
    shortfall: z.number(),
});
export const logMealResultSchema = z.object({
    id: z.string().uuid(),
    kitchen: kitchenTakeSchema.nullable(),
});
/**
 * Correct a logged meal in place: the slot, how much it was, or, for a
 * free-form entry, the figures the person gave. What it was (the food or
 * recipe) does not change; that is a delete and a new entry.
 */
export const updateMealLogSchema = z
    .object({
    slot: mealSlotSchema.optional(),
    quantity: z.number().positive().optional(),
    freeForm: z
        .object({
        description: z.string().min(1).max(200),
        energyKcal: z.number().positive().max(20000),
        proteinG: z.number().min(0).max(1000).nullish(),
        carbG: z.number().min(0).max(1000).nullish(),
        fatG: z.number().min(0).max(1000).nullish(),
    })
        .nullish(),
})
    .refine((v) => v.slot !== undefined || v.quantity !== undefined || v.freeForm != null, {
    message: 'Nothing to change',
});
/**
 * Keep a meal of logged foods as a recipe, so it can be logged again in one
 * go. The recipe is built on the server from that slot's food entries.
 */
export const mealToRecipeSchema = z.object({
    memberId: z.string().uuid(),
    date: z.string().date(),
    slot: mealSlotSchema,
    title: z.string().trim().min(1).max(200),
});
export const mealToRecipeResultSchema = z.object({
    recipeId: z.string().uuid(),
    title: z.string(),
    /** Food lines that went in. */
    ingredients: z.number().int(),
    /** Lines left out: recipes and free-form figures, which are not foods. */
    skipped: z.number().int(),
    /** The meal's photo became the recipe's picture. */
    withPhoto: z.boolean(),
});
/** A photograph of one person's meal on a day; the picture is fetched by id. */
export const mealPhotoSchema = z.object({
    id: z.string().uuid(),
    slot: mealSlotSchema,
    /** Changes when the photo is retaken, so a cached copy can be passed over. */
    updatedAt: z.string(),
});
// ------------------------------------------------------- AI proposals --
export const generatePlanRequestSchema = z.object({
    instructions: z.string().max(1000).nullish(),
    // Drinks are logged and planned by hand; the planner does not fill them (ADR-039).
    slots: z.array(mealSlotSchema.exclude(['custom', 'drink'])).min(1).optional(),
    dates: z.array(z.string().date()).min(1).max(7).optional(),
});
export const violationSchema = z.object({
    kind: z.enum([
        'unknown_recipe',
        'unknown_food',
        'unknown_member',
        'no_portion',
        'hard_constraint',
        'energy_out_of_range',
        'protein_below_floor',
    ]),
    slotIndex: z.number().int().optional(),
    date: z.string().optional(),
    memberId: z.string().optional(),
    message: z.string(),
});
export const proposalSchema = z.object({
    id: z.string().uuid(),
    planId: z.string().uuid(),
    status: z.enum(['pending', 'accepted', 'partly_accepted', 'rejected']),
    provider: z.string(),
    model: z.string(),
    /** How many repair rounds it took. Zero means right first time. */
    rounds: z.number().int(),
    instructions: z.string().nullable(),
    createdAt: z.date(),
    slots: z.array(z.object({
        index: z.number().int(),
        date: z.string(),
        slot: z.string(),
        recipeId: z.string().nullable(),
        foodId: z.string().nullable(),
        title: z.string(),
        memberNames: z.array(z.string()),
        portions: z.array(z.object({
            memberId: z.string(),
            servings: z.number().nullable(),
            grams: z.number().nullable(),
        })),
        rationale: z.string().nullable(),
    })),
    validation: z.object({
        valid: z.boolean(),
        violations: z.array(violationSchema),
        warnings: z.array(violationSchema),
        dayTotals: z.array(z.object({
            date: z.string(),
            memberId: z.string(),
            totals: nutrientsSchema,
            targetEnergyKcal: z.number().nullable(),
            targetProteinG: z.number().nullable(),
        })),
        inventoryUsage: z.array(z.object({
            foodId: z.string(),
            gramsPlanned: z.number(),
            gramsAvailable: z.number(),
            shortfall: z.number(),
        })),
        slotsToRetry: z.array(z.number().int()),
    }),
    batchCookNotes: z.string().nullable(),
    shoppingHints: z.array(z.string()),
    acceptedIndexes: z.array(z.number().int()),
});
export const acceptProposalSchema = z.object({
    /** Which slots to take. Omit to take everything the validator allowed. */
    indexes: z.array(z.number().int().min(0)).optional(),
    replaceExisting: z.boolean().default(true),
});
/** A glass or a bottle of water, logged as a drink (ADR-039). */
export const logWaterSchema = z.object({
    memberId: z.string().uuid(),
    date: z.string().date(),
    ml: z.number().int().min(1).max(5000),
});
//# sourceMappingURL=plan.js.map